import type { MailboxInfo } from 'owa-client-types';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getIsCopilotUIDisabled } from 'owa-mail-copilot-settings/lib/selectors/getIsCopilotUIDisabled';
import { isMailboxSharedOrDelegateUserMailbox } from 'owa-account-shared-mailbox-utils';
import hasBizChatLicense from './hasBizChatLicense';
import hasM365CopilotLicense from './hasM365CopilotLicense';

/**
 * Indicates whether Calendar search queries should be routed to Copilot instead of the standard
 * calendar search pipeline.
 *
 * @param mailboxInfo The mailbox being searched (not the global/primary mailbox)
 * @returns {boolean} True when the Copilot calendar search path is eligible (all internal prerequisites
 * are satisfied); false to use the standard (non-Copilot) calendar search path.
 */
export const isCalendarCopilotSearchEnabled = (mailboxInfo: MailboxInfo): boolean => {
    const isAIEnabled =
        isGlobalFeatureEnabled('mon-copilot-bizchat') &&
        (hasBizChatLicense(mailboxInfo) || hasM365CopilotLicense(mailboxInfo));
    const isSharedOrDelegateMailbox = isMailboxSharedOrDelegateUserMailbox(mailboxInfo);

    return (
        // 'cal-ai-search-with-chat': governing exposure flag for the Calendar Copilot search experience
        // (turning this flight/flag on is what actually lights up the feature to end users)
        isGlobalFeatureEnabled('cal-ai-search-with-chat') &&
        // Check that BizChat is enabled and user has proper AI SKU for this specific mailbox
        isAIEnabled &&
        // Do not show for shared or delegate mailboxes (similar to mail search)
        !isSharedOrDelegateMailbox &&
        // Do not show for archive mailboxes
        !(mailboxInfo.type === 'ArchiveMailbox') &&
        // Do not search with Copilot if all Copilot UI has been disabled for this mailbox
        !getIsCopilotUIDisabled(mailboxInfo)
    );
};
